import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { api } from '../../../lib/api';
import { toast } from 'sonner';
import type { Expense, ExpenseCategory, ExpenseType, WarehouseCar } from '../../../types/car';
import { useLanguage } from '../../../contexts/LanguageContext';

interface EditExpenseFormProps {
  expenseId: number;
  onSuccess: () => void;
}

function toLocalDateTimeISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}

export function EditExpenseForm({ expenseId, onSuccess }: EditExpenseFormProps) {
  const { t } = useLanguage();
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [warehouseCars, setWarehouseCars] = useState<WarehouseCar[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    expenseType: 'STORE' as ExpenseType,
    description: '',
    amount: '',
    currency: 'USD' as 'IQD' | 'USD',
    categoryId: '' as number | '',
    warehouseCarId: '' as number | '',
    expenseDate: '',
  });

  useEffect(() => {
    const fetchExpense = async () => {
      setLoading(true);
      try {
        const expense = (await api.get(`/api/expenses/${expenseId}`)) as Expense;
        const d = expense.expenseDate ? new Date(expense.expenseDate) : new Date();
        const dateStr = isNaN(d.getTime()) ? toLocalDateTimeISO(new Date()) : toLocalDateTimeISO(d);
        setForm({
          expenseType: (expense.expenseType ?? 'STORE') as ExpenseType,
          description: expense.description,
          amount: String(expense.amount),
          currency: expense.currency,
          categoryId: expense.categoryId ?? ('' as number | ''),
          warehouseCarId: expense.warehouseCarId ?? ('' as number | ''),
          expenseDate: dateStr,
        });
      } catch (err: unknown) {
        toast.error((err as { error?: string })?.error ?? t('expense.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchExpense();
  }, [expenseId]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.get('/api/expense-categories?active=true');
        setCategories(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      }
    };
    const fetchWarehouseCars = async () => {
      try {
        const data = await api.get('/api/warehouse-cars?status=IN_STOCK&limit=500');
        const list = (data as { warehouseCars?: WarehouseCar[] })?.warehouseCars ?? data;
        setWarehouseCars(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
      }
    };
    fetchCategories();
    fetchWarehouseCars();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.description.trim() || !form.amount || parseFloat(form.amount) <= 0) return;
    if (form.expenseType === 'CAR' && !form.warehouseCarId) {
      toast.error(t('expense.selectCarRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/api/expenses/${expenseId}`, {
        expenseType: form.expenseType,
        description: form.description.trim(),
        amount: parseFloat(form.amount),
        currency: form.currency,
        categoryId: form.expenseType === 'STORE' ? (form.categoryId || undefined) : undefined,
        warehouseCarId: form.expenseType === 'CAR' && form.warehouseCarId ? form.warehouseCarId : undefined,
        expenseDate: form.expenseDate ? new Date(form.expenseDate).toISOString() : undefined,
      });
      toast.success(t('expense.updated'));
      onSuccess();
    } catch (err: unknown) {
      toast.error((err as { error?: string })?.error ?? t('expense.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardContent className="py-12 text-center text-slate-500">{t('expense.loadingExpense')}</CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle>{t('expense.editTitle')}</CardTitle>
        <CardDescription>{t('expense.editDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expenseType">{t('expense.type')}</Label>
              <Select value={form.expenseType} disabled>
                <SelectTrigger id="expenseType" className="w-full border-slate-200 h-10 text-slate-700 bg-muted/50 cursor-not-allowed">
                  <SelectValue placeholder={t('expense.type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="STORE">{t('expense.storeType')}</SelectItem>
                  <SelectItem value="CAR">{t('expense.carType')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{t('expense.typeCannotChange')}</p>
            </div>
            {form.expenseType === 'CAR' && (
              <div className="space-y-2">
                <Label htmlFor="warehouseCar">{t('expense.carType')} *</Label>
                <Select
                  value={form.warehouseCarId === '' ? '__none__' : String(form.warehouseCarId)}
                  onValueChange={(v) => setForm((p) => ({ ...p, warehouseCarId: v === '__none__' ? '' : Number(v) }))}
                >
                  <SelectTrigger id="warehouseCar" className="w-full border-slate-200 h-10 text-slate-700">
                    <SelectValue placeholder="— Select car —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Select car —</SelectItem>
                    {warehouseCars.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        #{c.id} – {c.brand.name} {c.model.name} {c.year} {c.color}
                        {c.plateNumber ? ` (${c.plateNumber})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="description">{t('expense.description')} *</Label>
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder={
                  form.expenseType === 'CAR' ? t('expense.descriptionPlaceholderCar') : t('expense.descriptionPlaceholderStore')
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">{t('expense.amount')} *</Label>
              <Input
                id="amount"
                type="number"
                step="any"
                min="0"
                value={form.amount}
                onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                placeholder="0"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">{t('expense.currency')}</Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm((p) => ({ ...p, currency: v as 'IQD' | 'USD' }))}
              >
                <SelectTrigger id="currency" className="w-full border-slate-200 h-10 text-slate-700">
                  <SelectValue placeholder={t('expense.currency')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IQD">IQD</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expenseDate">{t('expense.dateTime')}</Label>
              <Input
                id="expenseDate"
                type="datetime-local"
                value={form.expenseDate}
                onChange={(e) => setForm((p) => ({ ...p, expenseDate: e.target.value }))}
                className="border-slate-200"
              />
            </div>
            {form.expenseType === 'STORE' && (
              <div className="space-y-2">
                <Label htmlFor="category">{t('expense.category')}</Label>
                <Select
                  value={form.categoryId === '' ? '__none__' : String(form.categoryId)}
                  onValueChange={(v) => setForm((p) => ({ ...p, categoryId: v === '__none__' ? '' : Number(v) }))}
                >
                  <SelectTrigger id="category" className="w-full border-slate-200 h-10 text-slate-700">
                    <SelectValue placeholder="— None —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 disabled:pointer-events-none"
            >
              {submitting ? t('expense.saving') : t('expense.saveChanges')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
